"use client";
import { useLanguageContext } from "@/context/LanguageContext";
import { Input, Tabs, Typography } from "antd";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { MyColumnType } from "@/app/components/MyTable";
import MyTable from "@/app/components/MyTable";
import { formatCurrency } from "@/app/utils/elipString";
import useAxiosQuery from "@/hooks/useMyQuery";
import { SearchOutlined } from "@ant-design/icons";
import type { FilterConfig, FilterValues, RangeFilter } from "@/app/components/TableFilter";
import FilterComponent from "@/app/components/TableFilter";
import debounce from "lodash/debounce";
import Overview from "@/app/components/exchange/Overview";

const ITEMS_PER_PAGE = 30;

interface IExchange {
  key: string;
  name: string;
  pairs_count: number;
  image: string;
  adjustedVolume: number;
  adjustedVolume24hPercentChange: number;
  jurisdiction: string | null;
  marketShare: number;
  numMarketCurrencies: number;
}

interface ExchangeTableProps {
  type: "DEX" | "CEX" | "Futures";
}

const ExchangeTable: React.FC<ExchangeTableProps> = ({ type }) => {
  const t = useTranslations("exchange");
  const { language } = useLanguageContext();

  const [displayData, setDisplayData] = useState<IExchange[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<FilterValues | null>(null);

  const { data: response, isLoading } = useAxiosQuery<{ data: IExchange[] }>({
    url: `v1/holder-info/exchange`,
    method: "get",
    params: { type },
    includeParamsInQueryKey: true,
  });

  const filterConfig = useMemo<FilterConfig>(
    () => ({
      numberFilters: [
        { key: "adjustedVolume", label: t("Trading_volume"), symbol: "$" },
        { key: "adjustedVolume24hPercentChange", label: t("Volume_percentage"), symbol: "%" },
        { key: "pairs_count", label: t("Pairs_count"), symbol: "" },
        { key: "marketShare", label: t("Market_share"), symbol: "%" },
        { key: "numMarketCurrencies", label: t("Coins"), symbol: "" },
      ],
    }),
    [t],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 300),
    [],
  );

  // Filter and search logic
  const filteredData = useMemo(() => {
    if (!response?.data) return [];

    let filtered = response.data;

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        exchange =>
          exchange.name.toLowerCase().includes(lowerSearchTerm) || exchange.key.toLowerCase().includes(lowerSearchTerm),
      );
    }

    // Apply active filters
    if (activeFilters) {
      filtered = filtered.filter(exchange => {
        // Number range filters
        const numberFields = {
          adjustedVolume: exchange.adjustedVolume,
          adjustedVolume24hPercentChange: exchange.adjustedVolume24hPercentChange,
          pairs_count: exchange.pairs_count,
          marketShare: exchange.marketShare,
          numMarketCurrencies: exchange.numMarketCurrencies,
        };

        for (const [key, range] of Object.entries(activeFilters)) {
          if (!Array.isArray(range) && key in numberFields && typeof range === "object") {
            const rangeFilter = range as RangeFilter;
            const value = numberFields[key as keyof typeof numberFields];
            if (rangeFilter.min && value < parseFloat(rangeFilter.min)) return false;
            if (rangeFilter.max && value > parseFloat(rangeFilter.max)) return false;
          }
        }
        return true;
      });
    }

    return filtered;
  }, [response?.data, searchTerm, activeFilters]);

  useEffect(() => {
    if (filteredData.length > 0) {
      setDisplayData(filteredData.slice(0, ITEMS_PER_PAGE));
      setHasMore(filteredData.length > ITEMS_PER_PAGE);
    }
  }, [filteredData]);

  const loadMoreData = useCallback(() => {
    if (loading || !hasMore || !filteredData) return;

    setLoading(true);

    setTimeout(() => {
      const currentSize = displayData.length;
      const nextData = filteredData.slice(0, currentSize + ITEMS_PER_PAGE);

      setDisplayData(nextData);
      setHasMore(nextData.length < filteredData.length);
      setLoading(false);
    }, 1000);
  }, [loading, hasMore, filteredData, displayData.length]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  const handleApplyFilters = useCallback((filters: FilterValues) => {
    setActiveFilters(filters);
  }, []);

  const columns = useMemo<MyColumnType<IExchange>[]>(
    () => [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        width: 50,
        render: (_: any, __: any, index: number) => (
          <Typography.Text type="secondary" className="text-sm">
            {index + 1}
          </Typography.Text>
        ),
        align: "center",
      },
      {
        title: t("Exchange"),
        key: "name",
        width: 200,
        dataIndex: "name",
        render: (text, record) => (
          <div
            className="flex items-center gap-2 cursor-pointer hover:text-blue-500"
            onClick={() => (window.location.href = `/${language}/exchange/${record.key}`)}
          >
            <img src={record.image} alt={text} className="w-6 h-6 rounded-full" />
            <span>{text}</span>
          </div>
        ),
      },
      {
        title: t("Trading_volume"),
        key: "adjustedVolume",
        width: 200,
        dataIndex: "adjustedVolume",
        render: text => (
          <Typography.Text title={String(text)} strong>
            {formatCurrency(text)}
          </Typography.Text>
        ),
        sorter: (a: IExchange, b: IExchange) => a.adjustedVolume - b.adjustedVolume,
      },
      {
        title: t("Volume_percentage"),
        key: "adjustedVolume24hPercentChange",
        dataIndex: "adjustedVolume24hPercentChange",
        width: 160,
        render: text =>
          text ? (
            text > 0 ? (
              <span className="text-green-500">{`${text.toFixed(2)}%`}</span>
            ) : (
              <span className="text-red-500">{`${text.toFixed(2)}%`}</span>
            )
          ) : (
            "-"
          ),
        sorter: (a: IExchange, b: IExchange) => a.adjustedVolume24hPercentChange - b.adjustedVolume24hPercentChange,
      },
      {
        title: t("Pairs_count"),
        key: "pairs_count",
        width: 150,
        dataIndex: "pairs_count",
        align: "right",
        sorter: (a: IExchange, b: IExchange) => a.pairs_count - b.pairs_count,
      },
      {
        title: t("Market_share"),
        key: "marketShare",
        width: 150,
        dataIndex: "marketShare",
        render: text => (text ? `${text.toFixed(2)}%` : "-"),
        sorter: (a: IExchange, b: IExchange) => a.marketShare - b.marketShare,
      },
      {
        title: t("Coins"),
        key: "numMarketCurrencies",
        dataIndex: "numMarketCurrencies",
        align: "right",
        width: 150,
        sorter: (a: IExchange, b: IExchange) => a.numMarketCurrencies - b.numMarketCurrencies,
      },
      {
        title: t("Country"),
        key: "jurisdiction",
        width: 200,
        dataIndex: "jurisdiction",
      },
    ],
    [t, language],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 justify-end">
        <Input
          placeholder={"Search exchange"}
          prefix={<SearchOutlined className="text-gray-400" />}
          onChange={handleSearch}
          className="flex-1 rounded-lg bg-white border-gray-300 text-gray-600 placeholder-gray-400 max-w-[400px]"
        />
        <FilterComponent onApplyFilters={handleApplyFilters} config={filterConfig} />
      </div>

      <MyTable
        scroll={{ x: 1200, y: 1200 }}
        className="w-full"
        columns={columns}
        dataSource={displayData}
        isLoading={isLoading}
        pagination={false}
        paginationMode="infinite"
        hasMore={hasMore}
        onLoadMore={loadMoreData}
        isLoadingMore={loading}
      />
    </div>
  );
};

// Individual table components using the base ExchangeTable
const CexTable = () => <ExchangeTable type="CEX" />;
const DexTable = () => <ExchangeTable type="DEX" />;
const FuturesTable = () => <ExchangeTable type="Futures" />;

const MainExchange = React.memo(() => {
  const t = useTranslations("exchange");
  const [activeTab, setActiveTab] = useState<string>("CEX");

  const tabItems = [
    {
      label: "CEX",
      tabKey: "CEX",
      key: "CEX",
      children: <CexTable />,
    },
    {
      label: "DEX",
      tabKey: "DEX",
      key: "DEX",
      children: <DexTable />,
    },
    {
      label: "Futures",
      tabKey: "Futures",
      key: "Futures",
      children: <FuturesTable />,
    },
  ];

  return (
    <div className="py-8">
      {/* overview */}
      <div className="mb-8">
        <Typography.Title className="text-2xl lg:text-3xl">{t("Top_cryptocurrency_exchange")}</Typography.Title>
        <Typography.Text type="secondary" className="text-lg">
          {t("Exchange_description_1")}
        </Typography.Text>
        <br />
        <Typography.Text type="secondary" className="text-lg">
          {t("Exchange_description_2")}{" "}
          <Typography.Link
            type="secondary"
            className="text-lg underline"
            target="_blank"
            href="https://docs.klarda.com"
          >
            {t("Click_here")}
          </Typography.Link>
        </Typography.Text>
      </div>
      <Overview />
      <Tabs items={tabItems} activeKey={activeTab} onTabClick={tab => setActiveTab(tab)} />
    </div>
  );
});

MainExchange.displayName = "MainExchange";
export default MainExchange;
